// The single place the product's price and its billing promise are written
// down. Every surface that shows a price — the landing section, the /pricing
// page, the programmatic-SEO country pages, the cabinet — imports from here, so
// the number and the promise cannot drift apart between them.
//
// The billing rule below is the offer's rule, not a paraphrase of it: change it
// only together with what the API actually charges for (the settlement path
// around the engine call in apps/api).

import type { ScanStatus } from "$lib/report/types";

/** The price of one recognised document, as it is shown to people. */
export const PRICE_PER_DOCUMENT = "1¢";

/** The same price written as an amount, for prose that needs a currency. */
export const PRICE_PER_DOCUMENT_USD = "$0.01";

/** The headline every price mention leads with. */
export const PRICE_HEADLINE = `${PRICE_PER_DOCUMENT} per document`;

/** The flat-price promise: one number, whatever the volume. */
export const PRICE_FLAT_NOTE =
  "Same price for everyone, at any volume – the first document and the ten-millionth cost the same.";

/** One sentence carrying both halves, for a meta description or a summary. */
export const PRICE_SENTENCE = `${PRICE_HEADLINE}. ${PRICE_FLAT_NOTE}`;

/** The headline of the billing promise. */
export const BILLED_ONLY_TITLE = "You pay only for documents we actually recognise.";

/** The body of the billing promise, in the reader's own terms. */
export const BILLED_ONLY_BODY =
  "Blurry photo, no document in frame, unknown type – free. You never pay for a failed scan.";

/** The promise compressed to one line, where a block does not fit. */
export const BILLED_ONLY_SHORT = "Only recognised documents are billed.";

/**
 * The exact rule that decides whether a call is charged. Kept precise on
 * purpose: it is the claim the price rests on, so it says what "recognised"
 * means rather than leaving it to the reader.
 */
export const BILLED_RULE =
  "A call is charged when the document type has been determined and data was " +
  "actually extracted from it: a machine-readable zone whose check digits pass, " +
  "at least five fields of the visual zone, or a correctly decoded barcode. " +
  "Nothing else is charged.";

/** What is never charged, listed the way a caller meets it. */
export const BILLED_NEVER =
  "No document found, an unreadable image, a type we do not support, an " +
  "internal error, a timeout – none of these cost anything.";

/** Where a caller reads the outcome for themselves. */
export const BILLED_FLAG_NOTE =
  "Every response carries a billed flag, so your own records match ours without a reconciliation step.";

/** How much recognition costs nothing, and on what footing. */
export const FREE_SANDBOX_ATTEMPTS = 10;
export const FREE_SANDBOX_REQUESTS_PER_HOUR = 10;
export const FREE_MONTHLY_DOCUMENTS = 100;

/**
 * The name the free monthly allowance is marketed under. "Free" is never
 * printed without the monthly number beside it, so the word cannot be read as
 * the whole product costing nothing.
 */
export const FREE_PLAN = `Free – ${FREE_MONTHLY_DOCUMENTS} documents every month`;

// The numbers above mirror the API's own allowances — DEFAULT_MAX_FREE_ATTEMPTS
// and SANDBOX_RATE_LIMIT in apps/api/src/config, and FREE_MONTHLY_CREDITS in
// apps/api/src/billing/allowance.ts — which the web app cannot import across the
// HTTP boundary. A deployment that moves one moves these too. The hourly
// ceiling counts every sandbox request, whatever it is answered, which is why
// it is stated beside the allowance.
export const FREE_START_SANDBOX = `${FREE_SANDBOX_ATTEMPTS} free recognised documents per address on the public sandbox key, with no account at all – at most ${FREE_SANDBOX_REQUESTS_PER_HOUR} requests an hour, whatever their answer.`;
export const FREE_START_REGISTERED = `${FREE_PLAN} on your own account, once you confirm your email – back to ${FREE_MONTHLY_DOCUMENTS} at 00:00 UTC on the 1st. No card needed.`;
export const FREE_START_TOPUP =
  "Beyond the month's free documents, you top up the balance and every recognised document draws one paid credit from it.";

/** One outcome of a call, and whether it is charged. */
export interface BillingOutcome {
  readonly label: string;
  readonly billed: boolean;
  /** What the caller sent, in their terms — not the engine's. */
  readonly meaning: string;
}

/**
 * Every status the API can answer with, against what it costs. Typed as a total
 * map of the contract's status union, so a status added to the contract fails
 * the build here rather than quietly appearing on the page as an unpriced
 * outcome.
 */
export const BILLING_OUTCOMES: Readonly<Record<ScanStatus, BillingOutcome>> = {
  recognized: {
    label: "Recognized",
    billed: true,
    meaning: "The type was determined and the data came out. This is the only outcome you pay for.",
  },
  no_document_found: {
    label: "No document found",
    billed: false,
    meaning: "Nothing document-shaped was in the frame.",
  },
  unreadable: {
    label: "Unreadable",
    billed: false,
    meaning: "A document was there, but no usable text could be read off it.",
  },
  unsupported_document: {
    label: "Unsupported document",
    billed: false,
    meaning: "The document was found, but its type is not one we know.",
  },
  rejected: {
    label: "Rejected",
    billed: false,
    meaning: "Recognition failed on our side – an internal error or a timeout.",
  },
};

/** The outcomes in the order they are shown: what you pay for, then what you do not. */
export const BILLING_OUTCOME_ORDER: readonly ScanStatus[] = [
  "recognized",
  "no_document_found",
  "unreadable",
  "unsupported_document",
  "rejected",
];
